import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import "./WaitlistModal.scss";

/**
 * Waitlist modal (light version of the soon.opencampus.xyz form).
 *
 * variant:
 *  - "fastTrack" — AI Fast Track cohort waitlist (Hero / People CTAs)
 *  - "minds"     — OC Minds early-access waitlist (OcMinds "Try …" / bottom CTA)
 *
 * Reuses the same submission setup as soon.opencampus.xyz: the form POSTs JSON
 * to a Google Apps Script endpoint. `text/plain` is CORS-safelisted, so the
 * browser skips the preflight OPTIONS request that Apps Script can't answer;
 * Apps Script still receives the raw JSON via e.postData.contents.
 */
const SCRIPT_URL =
  process.env.REACT_APP_WAITLIST_SCRIPT_URL ||
  "https://script.google.com/macros/s/AKfycbyp2MQK3a5xskoR2m7NRFi4IEJ9LIlJL9DcPd8b6l-mCx3FfQ6vYPU1EoeTWWv3u1aW/exec";

const COUNTRIES = [
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Argentina", "Armenia", "Australia",
  "Austria", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Belarus", "Belgium", "Belize",
  "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei",
  "Bulgaria", "Burkina Faso", "Burundi", "Cambodia", "Cameroon", "Canada", "Cape Verde",
  "Central African Republic", "Chad", "Chile", "China", "Colombia", "Comoros", "Congo",
  "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czech Republic", "Denmark", "Djibouti",
  "Dominican Republic", "DR Congo", "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea",
  "Eritrea", "Estonia", "Eswatini", "Ethiopia", "Fiji", "Finland", "France", "Gabon", "Gambia",
  "Georgia", "Germany", "Ghana", "Greece", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana",
  "Haiti", "Honduras", "Hong Kong", "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland",
  "Israel", "Italy", "Ivory Coast", "Jamaica", "Japan", "Jordan", "Kazakhstan", "Kenya",
  "Kosovo", "Kuwait", "Kyrgyzstan", "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya",
  "Liechtenstein", "Lithuania", "Luxembourg", "Madagascar", "Malawi", "Malaysia", "Maldives",
  "Mali", "Malta", "Mauritania", "Mauritius", "Mexico", "Moldova", "Monaco", "Mongolia",
  "Montenegro", "Morocco", "Mozambique", "Myanmar", "Namibia", "Nepal", "Netherlands",
  "New Zealand", "Nicaragua", "Niger", "Nigeria", "North Korea", "North Macedonia", "Norway",
  "Oman", "Pakistan", "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru",
  "Philippines", "Poland", "Portugal", "Qatar", "Romania", "Russia", "Rwanda",
  "Saudi Arabia", "Senegal", "Serbia", "Sierra Leone", "Singapore", "Slovakia", "Slovenia",
  "Somalia", "South Africa", "South Korea", "South Sudan", "Spain", "Sri Lanka", "Sudan",
  "Suriname", "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan", "Tanzania", "Thailand",
  "Timor-Leste", "Togo", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Uganda",
  "Ukraine", "United Arab Emirates", "United Kingdom", "United States", "Uruguay", "Uzbekistan",
  "Venezuela", "Vietnam", "Yemen", "Zambia", "Zimbabwe",
];

const COPY = {
  fastTrack: {
    program: "AI Fast Track",
    promo: (
      <>
        The first cohort of our AI Fast Track program is launching soon. Learn to
        ship using AI, build alongside others, and unlock internships, paid
        challenges, and more.{" "}
        <strong>
          Sign up now to be the first to know when applications open.
        </strong>
      </>
    ),
    success:
      "Thanks for signing up. We'll be in touch the moment applications for the AI Fast Track program open.",
    extraLabel: "What's your current experience with AI?",
    extraPlaceholder: "Tell us where you're starting from…",
    goalsLabel:
      "What are the top three things you hope to get out of the AI Fast Track program?",
    goalsPlaceholder: "1. …\n2. …\n3. …",
    showGoals: true,
  },
  minds: {
    program: "OC Minds",
    promo: (
      <>
        The Open Campus web app is currently in closed beta.{" "}
        <strong>
          Sign up for a chance to be invited to test it out and give your feedback.
        </strong>
      </>
    ),
    success:
      "Thanks for signing up. We'll email you once the Open Campus web app is available for you to try out.",
    extraLabel: "What are you most excited to use a Mind for?",
    extraPlaceholder: "Tell us what you have in mind…",
    showGoals: false,
  },
};

export default function WaitlistModal({ open, onClose, variant = "fastTrack" }) {
  const copy = COPY[variant] || COPY.fastTrack;
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [country, setCountry] = useState("");
  const [aiExperience, setAiExperience] = useState("");
  const [goals, setGoals] = useState("");
  const [privacy, setPrivacy] = useState(false);
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  useEffect(() => {
    if (open) {
      setFullName("");
      setEmail("");
      setCountry("");
      setAiExperience("");
      setGoals("");
      setPrivacy(false);
      setStatus("idle");
      setErrorMsg("");
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!privacy) return;
    setStatus("loading");
    setErrorMsg("");
    try {
      const res = await fetch(SCRIPT_URL, {
        method: "POST",
        headers: { "Content-Type": "text/plain;charset=utf-8" },
        body: JSON.stringify({
          fullName,
          email,
          country,
          aiExperience,
          topGoals: goals,
          excitedAbout: copy.showGoals ? goals : aiExperience,
          program: copy.program,
          timestamp: new Date().toISOString(),
        }),
      });
      const data = await res.json();
      if (data.result === "success") {
        setStatus("success");
      } else {
        throw new Error(data.error || "Unknown error");
      }
    } catch (err) {
      setStatus("error");
      setErrorMsg(
        err instanceof Error ? err.message : "Something went wrong. Please try again."
      );
    }
  };

  if (!open) return null;

  // Portal to <body> so position:fixed + scrolling aren't trapped by homepage
  // sections that use transform / will-change / overflow:hidden (scroll-reveal).
  return createPortal(
    <div
      className="wl-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="wl-modal-title"
      onMouseDown={onClose}
    >
      {/* Centering wrapper grows with the card so the OUTER .wl-modal can
          scroll the full form when it's taller than the viewport. */}
      <div className="wl-modal__center">
      <div className="wl-modal__card" onMouseDown={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="wl-modal__close"
          aria-label="Close"
          onClick={onClose}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
            <path
              d="M1 1l14 14M15 1L1 15"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        </button>

        {status === "success" ? (
          <div className="wl-modal__success">
            <div className="wl-modal__success-icon" aria-hidden="true">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </div>
            <h3 className="wl-modal__title">You&rsquo;re on the list!</h3>
            <p className="wl-modal__text">{copy.success}</p>
            <button type="button" className="wl-modal__btn" onClick={onClose}>
              Done
            </button>
          </div>
        ) : (
          <>
            <h3 id="wl-modal-title" className="wl-modal__title">
              Join the waitlist
            </h3>

            <div className="wl-modal__promo">{copy.promo}</div>

            <form className="wl-modal__form" onSubmit={handleSubmit}>
              <div className="wl-field">
                <label className="wl-field__label" htmlFor="wl-fullname">
                  Full name
                </label>
                <input
                  id="wl-fullname"
                  className="wl-field__input"
                  type="text"
                  required
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Jane Doe"
                />
              </div>

              <div className="wl-field">
                <label className="wl-field__label" htmlFor="wl-email">
                  Email
                </label>
                <input
                  id="wl-email"
                  className="wl-field__input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </div>

              <div className="wl-field">
                <label className="wl-field__label" htmlFor="wl-country">
                  Country / Region
                </label>
                <div className="wl-field__select-wrap">
                  <select
                    id="wl-country"
                    className={`wl-field__select${country ? "" : " wl-field__select--empty"}`}
                    required
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                  >
                    <option value="" disabled>
                      Select your country / region
                    </option>
                    {COUNTRIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                  <svg className="wl-field__caret" width="12" height="7" viewBox="0 0 12 7" fill="none" aria-hidden="true">
                    <path d="M1 1l5 4.5L11 1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              </div>

              <div className="wl-field">
                <label className="wl-field__label" htmlFor="wl-experience">
                  {copy.extraLabel}
                </label>
                <textarea
                  id="wl-experience"
                  className="wl-field__textarea"
                  rows={3}
                  value={aiExperience}
                  onChange={(e) => setAiExperience(e.target.value)}
                  placeholder={copy.extraPlaceholder}
                />
              </div>

              {copy.showGoals && (
                <div className="wl-field">
                  <label className="wl-field__label" htmlFor="wl-goals">
                    {copy.goalsLabel}
                  </label>
                  <textarea
                    id="wl-goals"
                    className="wl-field__textarea"
                    rows={3}
                    value={goals}
                    onChange={(e) => setGoals(e.target.value)}
                    placeholder={copy.goalsPlaceholder}
                  />
                </div>
              )}

              <label className="wl-field__privacy" htmlFor="wl-privacy">
                <input
                  id="wl-privacy"
                  type="checkbox"
                  required
                  checked={privacy}
                  onChange={(e) => setPrivacy(e.target.checked)}
                />
                <span>
                  I acknowledge and agree to the{" "}
                  <a
                    href="https://legal.opencampus.xyz/privacy/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </a>
                  .
                </span>
              </label>

              {errorMsg ? <p className="wl-modal__error">{errorMsg}</p> : null}

              <button
                type="submit"
                className="wl-modal__btn"
                disabled={status === "loading" || !privacy}
              >
                {status === "loading" ? "Submitting…" : "Join the waitlist"}
              </button>
            </form>
          </>
        )}
      </div>
      </div>
    </div>,
    document.body
  );
}
